import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CustomerClubTour from '../components/CustomerClubTour';
import CustomerClubSegmentsEditor, {
  ClubSegmentConfig,
} from '../components/CustomerClubSegmentsEditor';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessCustomerClub, canEditCustomerClubSegments } from '../utils/customerClubAccess';
import { formatRial } from '../utils/formatMoneyFa';

const PAGE_SIZE = 50;
const EXPORT_LIMIT = 2000;
const TOUR_STORAGE_KEY = 'customer_club_tour_seen_v1';

const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

type Period = '12m' | '2y' | '3y' | '5y' | 'all';

const PERIOD_OPTIONS: { key: Period; label: string }[] = [
  { key: '12m', label: '۱۲ ماه' },
  { key: '2y', label: '۲ سال' },
  { key: '3y', label: '۳ سال' },
  { key: '5y', label: '۵ سال' },
  { key: 'all', label: 'همه (از ۱۳۹۲)' },
];

const COLOR_STYLES: Record<string, { bar: string; surface: string; activeRing: string }> = {
  indigo: {
    bar: 'bg-indigo-500',
    surface: 'border-indigo-100/80 bg-gradient-to-br from-indigo-50/90 via-white to-white ring-indigo-100/60',
    activeRing: 'ring-2 ring-indigo-400',
  },
  amber: {
    bar: 'bg-amber-500',
    surface: 'border-amber-100/80 bg-gradient-to-br from-amber-50/90 via-white to-white ring-amber-100/60',
    activeRing: 'ring-2 ring-amber-400',
  },
  orange: {
    bar: 'bg-orange-500',
    surface: 'border-orange-100/80 bg-gradient-to-br from-orange-50/90 via-white to-white ring-orange-100/60',
    activeRing: 'ring-2 ring-orange-400',
  },
  emerald: {
    bar: 'bg-emerald-500',
    surface: 'border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-white ring-emerald-100/60',
    activeRing: 'ring-2 ring-emerald-400',
  },
  rose: {
    bar: 'bg-rose-500',
    surface: 'border-rose-100/80 bg-gradient-to-br from-rose-50/90 via-white to-white ring-rose-100/60',
    activeRing: 'ring-2 ring-rose-400',
  },
  sky: {
    bar: 'bg-sky-500',
    surface: 'border-sky-100/80 bg-gradient-to-br from-sky-50/90 via-white to-white ring-sky-100/60',
    activeRing: 'ring-2 ring-sky-400',
  },
  violet: {
    bar: 'bg-violet-500',
    surface: 'border-violet-100/80 bg-gradient-to-br from-violet-50/90 via-white to-white ring-violet-100/60',
    activeRing: 'ring-2 ring-violet-400',
  },
  slate: {
    bar: 'bg-slate-500',
    surface: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white ring-slate-200',
    activeRing: 'ring-2 ring-slate-400',
  },
};

interface ClubSummary {
  period: Period;
  total_with_completed: number;
  best?: number;
  forgotten?: number;
  at_risk?: number;
  counts?: Record<string, number>;
  segments?: ClubSegmentConfig[];
  best_source?: string;
  his_range?: { from_date: string; to_date: string };
  his_error?: string | null;
}

interface ClubMember {
  user_id?: number | null;
  patient_info_id?: number | null;
  source?: 'his' | 'booking';
  full_name: string;
  phone: string;
  national_id?: string | null;
  completed_count: number;
  lifetime_completed: number;
  cancelled_count: number;
  open_count: number;
  last_appointment_date?: string | null;
  last_completed_at?: string | null;
  days_since_last?: number | null;
  top_services: string[];
  total_amount?: number | null;
}

interface HisVisit {
  patient_id: number;
  recept_date?: string | null;
  record_code?: string | null;
  total_amount?: number | null;
  naghdi?: number | null;
  bime?: number | null;
  insurance_name?: string | null;
  insurance_name_a?: string | null;
  insurance_name_b?: string | null;
  service_names: string[];
}

interface ClubAppointment {
  id: number;
  tracking_code?: string | null;
  status?: string | null;
  appointment_date?: string | null;
  appointment_time?: string | null;
  created_at?: string | null;
  cancel_reason?: string | null;
  service_names: string[];
}

interface ClubProfile {
  source?: 'his' | 'booking';
  user_id?: number | null;
  patient_info_id?: number | null;
  full_name: string;
  phone: string;
  national_id?: string | null;
  created_at?: string | null;
  total_amount?: number | null;
  total_naghdi?: number | null;
  total_bime?: number | null;
  counts: {
    completed: number;
    in_progress: number;
    cancelled: number;
    pending: number;
    incomplete: number;
    total: number;
  };
  appointments: ClubAppointment[];
  his_visits?: HisVisit[];
}

const formatDaysSince = (days?: number | null): string => {
  if (days === null || days === undefined) return '—';
  if (days < 0) return `${toPersianDigits(String(Math.abs(days)))} روز مانده`;
  if (days === 0) return 'امروز';
  return `${toPersianDigits(String(days))} روز پیش`;
};

const csvEscape = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

type SortKey =
  | 'full_name'
  | 'phone'
  | 'national_id'
  | 'completed_count'
  | 'total_amount'
  | 'last_appointment_date'
  | 'days_since_last'
  | 'top_services';
type SortDir = 'asc' | 'desc';

const SORTABLE_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'full_name', label: 'نام' },
  { key: 'phone', label: 'موبایل' },
  { key: 'national_id', label: 'کد ملی' },
  { key: 'completed_count', label: 'پذیرش HIS' },
  { key: 'total_amount', label: 'مبلغ (فقط نمایش)' },
  { key: 'last_appointment_date', label: 'آخرین پذیرش' },
  { key: 'days_since_last', label: 'فاصله' },
  { key: 'top_services', label: 'خدمات پرتکرار' },
];

const FALLBACK_SEGMENTS: ClubSegmentConfig[] = [
  {
    id: 'best',
    label: 'مشتری VIP',
    color: 'indigo',
    enabled: true,
    min_visits: 4,
    max_visits: null,
    min_months_since: null,
    max_months_since: 4,
    min_days_since: null,
    max_days_since: null,
    hint: 'حداقل ۴ پذیرش HIS؛ آخرین مراجعه حداکثر ۴ ماه پیش',
  },
  {
    id: 'forgotten',
    label: 'فراموش‌شده‌های خوب',
    color: 'amber',
    enabled: true,
    min_visits: 4,
    max_visits: null,
    min_months_since: 3,
    max_months_since: null,
    min_days_since: null,
    max_days_since: null,
    hint: 'حداقل ۴ پذیرش HIS؛ بیش از ۳ ماه بدون مراجعه',
  },
  {
    id: 'at_risk',
    label: 'در آستانه ریزش',
    color: 'orange',
    enabled: true,
    min_visits: 2,
    max_visits: 3,
    min_months_since: null,
    max_months_since: 6,
    min_days_since: 90,
    max_days_since: null,
    hint: 'بین ۲ تا ۳ پذیرش HIS؛ حداقل ۹۰ روز بدون مراجعه',
  },
];

const AdminCustomerClub: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('12m');
  const [segment, setSegment] = useState<string>('best');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<ClubSummary | null>(null);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [profile, setProfile] = useState<ClubProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [segmentCards, setSegmentCards] = useState<ClubSegmentConfig[]>(FALLBACK_SEGMENTS);
  const [allSegments, setAllSegments] = useState<ClubSegmentConfig[]>(FALLBACK_SEGMENTS);
  const [defaultsConfig, setDefaultsConfig] = useState<ClubSegmentConfig[]>(FALLBACK_SEGMENTS);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [minSegments, setMinSegments] = useState(2);
  const [maxSegments, setMaxSegments] = useState(6);
  const [sortBy, setSortBy] = useState<SortKey>('last_appointment_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const canEditSegments = canEditCustomerClubSegments();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessCustomerClub()) {
      navigate('/admin/dashboard');
      return;
    }
    if (!localStorage.getItem(TOUR_STORAGE_KEY)) {
      setTourOpen(true);
    }
  }, [navigate]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadSegmentConfig = useCallback(async () => {
    const apiBaseUrl = getApiBaseUrl();
    const res = await fetch(`${apiBaseUrl}/admin/customer-club/segments-config`, {
      headers: authHeaders(),
    });
    if (!res.ok) return;
    const data = await res.json();
    const segs = (data.segments || []) as ClubSegmentConfig[];
    const defs = (data.defaults || []) as ClubSegmentConfig[];
    if (segs.length) {
      setAllSegments(segs);
      setSegmentCards(segs.filter((s) => s.enabled !== false));
    }
    if (defs.length) setDefaultsConfig(defs);
    if (data.min_segments) setMinSegments(Number(data.min_segments));
    if (data.max_segments) setMaxSegments(Number(data.max_segments));
    setSegment((prev) => {
      const ids = segs.filter((s) => s.enabled !== false).map((s) => s.id);
      return ids.includes(prev) ? prev : ids[0] || 'best';
    });
  }, [authHeaders]);

  useEffect(() => {
    void loadSegmentConfig();
  }, [loadSegmentConfig]);

  const fetchSummary = useCallback(async () => {
    const apiBaseUrl = getApiBaseUrl();
    const params = new URLSearchParams({
      period,
    });
    const res = await fetch(`${apiBaseUrl}/admin/customer-club/summary?${params}`, {
      headers: authHeaders(),
    });
    if (res.status === 401) {
      navigate('/admin/login');
      return;
    }
    if (res.status === 403) {
      navigate('/admin/dashboard');
      return;
    }
    if (!res.ok) {
      throw new Error('خطا در دریافت خلاصه باشگاه مشتریان');
    }
    const data = (await res.json()) as ClubSummary;
    setSummary(data);
    if (data.segments && data.segments.length) {
      const enabled = data.segments.filter((s) => s.enabled !== false);
      setSegmentCards(enabled);
      setSegment((prev) => (enabled.some((s) => s.id === prev) ? prev : enabled[0]?.id || prev));
    }
    if (data.his_error) {
      setError(`خواندن HIS: ${data.his_error}`);
    }
  }, [authHeaders, navigate, period]);

  const fetchMembers = useCallback(async () => {
    const apiBaseUrl = getApiBaseUrl();
    const skip = (page - 1) * PAGE_SIZE;
    const params = new URLSearchParams({
      segment,
      period,
      skip: String(skip),
      limit: String(PAGE_SIZE),
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (debouncedQuery) params.set('q', debouncedQuery);
    const res = await fetch(`${apiBaseUrl}/admin/customer-club/members?${params}`, {
      headers: authHeaders(),
    });
    if (res.status === 401) {
      navigate('/admin/login');
      return;
    }
    if (res.status === 403) {
      navigate('/admin/dashboard');
      return;
    }
    if (!res.ok) {
      throw new Error('خطا در دریافت لیست مشتریان');
    }
    const totalHeader = res.headers.get('X-Total-Count');
    setTotal(totalHeader ? Number(totalHeader) || 0 : 0);
    const data = (await res.json()) as ClubMember[];
    setMembers(Array.isArray(data) ? data : []);
  }, [authHeaders, debouncedQuery, navigate, page, period, segment, sortBy, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(
        key === 'full_name' || key === 'phone' || key === 'national_id' || key === 'top_services'
          ? 'asc'
          : 'desc'
      );
    }
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([fetchSummary(), fetchMembers()]);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'خطا در بارگذاری داده‌ها');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [fetchMembers, fetchSummary]);

  const closeTour = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, '1');
    setTourOpen(false);
  };

  const openProfile = async (row: ClubMember) => {
    setProfileLoading(true);
    setProfile(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      if (!row.patient_info_id) {
        throw new Error('شناسه بیمار HIS موجود نیست');
      }
      const url = `${apiBaseUrl}/admin/customer-club/his-members/${row.patient_info_id}?period=${period}`;
      const res = await fetch(url, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error('خطا در دریافت جزئیات مشتری');
      }
      const data = (await res.json()) as ClubProfile;
      setProfile(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در دریافت جزئیات مشتری');
    } finally {
      setProfileLoading(false);
    }
  };

  const downloadCsv = async () => {
    setExporting(true);
    setError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({
        segment,
        period,
        skip: '0',
        limit: String(EXPORT_LIMIT),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (debouncedQuery) params.set('q', debouncedQuery);
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/members?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error('خطا در تهیه خروجی');
      }
      const rows = (await res.json()) as ClubMember[];
      const header = [
        'رتبه',
        'نام',
        'موبایل',
        'کد ملی',
        'نوبت تکمیل بازه',
        'نوبت تکمیل کل',
        'لغو',
        'نوبت باز',
        'مبلغ HIS (ریال)',
        'آخرین نوبت',
        'روز از آخرین مراجعه',
        'خدمات پرتکرار',
      ];
      const lines = [header.join(',')];
      rows.forEach((row, idx) => {
        lines.push(
          [
            String(idx + 1),
            csvEscape(row.full_name || ''),
            csvEscape(row.phone || ''),
            csvEscape(row.national_id || ''),
            String(row.completed_count ?? 0),
            String(row.lifetime_completed ?? 0),
            String(row.cancelled_count ?? 0),
            String(row.open_count ?? 0),
            row.total_amount === null || row.total_amount === undefined
              ? ''
              : String(Math.round(row.total_amount)),
            csvEscape(row.last_appointment_date || ''),
            row.days_since_last === null || row.days_since_last === undefined
              ? ''
              : String(row.days_since_last),
            csvEscape((row.top_services || []).join('، ')),
          ].join(',')
        );
      });
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `باشگاه_مشتریان_${segment}_${period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'خطا در تهیه خروجی');
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activeCard = segmentCards.find((c) => c.id === segment);

  const segmentCount = (segId: string): number => {
    if (!summary) return 0;
    if (summary.counts && typeof summary.counts[segId] === 'number') return summary.counts[segId];
    const fromList = summary.segments?.find((s) => s.id === segId)?.count;
    if (typeof fromList === 'number') return fromList;
    const legacy = (summary as unknown as Record<string, unknown>)[segId];
    return typeof legacy === 'number' ? legacy : 0;
  };

  const gridClass = useMemo(() => {
    const n = Math.max(1, segmentCards.length);
    if (n <= 2) return 'grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4';
    if (n === 3) return 'grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4';
    return 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 sm:gap-4';
  }, [segmentCards.length]);

  const saveSegments = async (segments: ClubSegmentConfig[]) => {
    setEditorSaving(true);
    setEditorError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/segments-config`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ segments }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'ذخیره سگمنت‌ها ناموفق بود');
      }
      const data = await res.json();
      const saved = (data.segments || []) as ClubSegmentConfig[];
      setAllSegments(saved);
      const enabled = saved.filter((s) => s.enabled !== false);
      setSegmentCards(enabled);
      setSegment((prev) => (enabled.some((s) => s.id === prev) ? prev : enabled[0]?.id || 'best'));
      setEditorOpen(false);
      setPage(1);
      setLoading(true);
      await Promise.all([fetchSummary(), fetchMembers()]);
    } catch (err: unknown) {
      setEditorError(err instanceof Error ? err.message : 'خطا در ذخیره');
    } finally {
      setEditorSaving(false);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-base text-gray-900" style={{ direction: 'rtl' }}>
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-l from-violet-600 via-indigo-700 to-slate-800 px-5 py-5 text-white shadow-lg shadow-indigo-900/20 sm:px-6">
        <div
          className="pointer-events-none absolute -left-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">سگمنت‌های باشگاه مشتریان</h1>
            <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-indigo-100/95">
              سگمنت‌های قابل‌شخصی‌سازی بر اساس پذیرش واقعی HIS. تعداد کارت‌ها و قوانین هر کدام را خودتان تنظیم کنید.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEditSegments ? (
              <button
                type="button"
                onClick={() => {
                  setEditorError(null);
                  setEditorOpen(true);
                }}
                className="shrink-0 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20"
              >
                تنظیم سگمنت‌ها
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setTourOpen(true)}
              className="shrink-0 rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium text-white backdrop-blur hover:bg-white/20"
            >
              راهنمای صفحه
            </button>
          </div>
        </div>
      </section>

      <section
        data-tour="period"
        className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-700">بازه شمارش مراجعات</span>
                <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-gray-50 p-1">
                  {PERIOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setPeriod(opt.key);
                        setPage(1);
                      }}
                      className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                        period === opt.key
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex min-w-[16rem] flex-col gap-1.5">
                <span className="text-sm font-medium text-gray-700">جستجو</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder="نام، موبایل یا کد ملی"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => void downloadCsv()}
              disabled={exporting}
              className="inline-flex items-center justify-center rounded-lg bg-gradient-to-b from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-medium text-white shadow-md ring-1 ring-blue-500/30 hover:from-blue-500 hover:to-blue-600 disabled:opacity-60"
            >
              {exporting ? 'در حال آماده‌سازی…' : 'دانلود اکسل سگمنت'}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base leading-relaxed text-red-900">
          {error}
        </div>
      )}

      <div data-tour="segments" className={gridClass}>
        {segmentCards.map((card) => {
          const styles = COLOR_STYLES[card.color] || COLOR_STYLES.indigo;
          const value = segmentCount(card.id);
          const active = segment === card.id;
          return (
            <button
              type="button"
              key={card.id}
              onClick={() => {
                setSegment(card.id);
                setPage(1);
              }}
              className={`relative overflow-hidden rounded-2xl border p-5 text-right shadow-md shadow-gray-200/30 ring-1 transition hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${styles.surface} ${
                active ? styles.activeRing : ''
              }`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 ${styles.bar}`} aria-hidden />
              <p className="text-sm font-medium text-gray-600 sm:text-base">{card.label}</p>
              <p className="mt-3 text-3xl font-bold tabular-nums tracking-tight text-gray-900 sm:text-4xl">
                {value.toLocaleString('fa-IR')}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">{card.hint}</p>
            </button>
          );
        })}
      </div>

      <section
        data-tour="table"
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{activeCard?.label}</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {toPersianDigits(String(total))} مشتری از HIS
              {summary?.his_range
                ? ` (${toPersianDigits(summary.his_range.from_date)} تا ${toPersianDigits(summary.his_range.to_date)})`
                : ''}
              {' — '}
              برای مرتب‌سازی روی عنوان ستون کلیک کنید
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner text="در حال بارگذاری باشگاه مشتریان..." />
        ) : members.length === 0 ? (
          <p className="px-5 py-10 text-center text-sm text-gray-500">مشتری‌ای در این سگمنت یافت نشد.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ direction: 'rtl' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">رتبه</th>
                  {SORTABLE_COLUMNS.map((col) => {
                    const active = sortBy === col.key;
                    return (
                      <th key={col.key} className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSort(col.key);
                          }}
                          className={`inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition hover:bg-gray-100 hover:text-gray-800 ${
                            active ? 'font-semibold text-indigo-700' : ''
                          }`}
                        >
                          <span>{col.label}</span>
                          <span className="text-[10px] tabular-nums text-gray-400" aria-hidden>
                            {active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {members.map((row, idx) => (
                  <tr
                    key={row.patient_info_id || row.user_id}
                    onClick={() => void openProfile(row)}
                    className="cursor-pointer hover:bg-indigo-50/40"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-500">
                      {toPersianDigits(String((page - 1) * PAGE_SIZE + idx + 1))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {row.full_name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700" dir="ltr">
                      {toPersianDigits(row.phone || '')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700" dir="ltr">
                      {row.national_id ? toPersianDigits(row.national_id) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold tabular-nums text-gray-900">
                      {row.completed_count.toLocaleString('fa-IR')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-900">
                      {row.total_amount != null ? formatRial(row.total_amount) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-gray-700">
                      {row.last_appointment_date
                        ? toPersianDigits(row.last_appointment_date)
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatDaysSince(row.days_since_last)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {(row.top_services || []).join('، ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-700 disabled:opacity-40"
            >
              قبلی
            </button>
            <span className="tabular-nums text-gray-600">
              صفحه {toPersianDigits(String(page))} از {toPersianDigits(String(pageCount))}
            </span>
            <button
              type="button"
              disabled={page >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-700 disabled:opacity-40"
            >
              بعدی
            </button>
          </div>
        )}
      </section>

      {(profileLoading || profile) && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 px-3 py-8 backdrop-blur-[2px]"
          onClick={() => {
            if (!profileLoading) setProfile(null);
          }}
        >
          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl shadow-slate-900/20"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            {profileLoading || !profile ? (
              <div className="p-8">
                <LoadingSpinner text="در حال بارگذاری پرونده مشتری..." />
              </div>
            ) : (
              <>
                <div className="relative overflow-hidden bg-gradient-to-l from-indigo-700 via-violet-700 to-slate-800 px-5 py-5 text-white sm:px-6">
                  <div
                    className="pointer-events-none absolute -left-10 -top-16 h-40 w-40 rounded-full bg-white/10 blur-2xl"
                    aria-hidden
                  />
                  <div className="relative flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-indigo-100/90">
                        {profile.source === 'his' ? 'پرونده پذیرش HIS' : 'پرونده مشتری'}
                      </p>
                      <h3 className="mt-1 truncate text-xl font-bold tracking-tight sm:text-2xl">
                        {profile.full_name}
                      </h3>
                      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-indigo-100/95">
                        <span dir="ltr">{toPersianDigits(profile.phone)}</span>
                        {profile.national_id ? (
                          <span>کد ملی {toPersianDigits(profile.national_id)}</span>
                        ) : null}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setProfile(null)}
                      className="shrink-0 rounded-xl border border-white/25 bg-white/10 px-3 py-1.5 text-sm text-white backdrop-blur hover:bg-white/20"
                    >
                      بستن
                    </button>
                  </div>
                </div>

                <div className="space-y-5 p-5 sm:p-6">
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <div className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white px-4 py-3">
                      <p className="text-xs font-medium text-indigo-700/80">پذیرش HIS</p>
                      <p className="mt-1 text-2xl font-bold tabular-nums text-indigo-950">
                        {profile.counts.completed.toLocaleString('fa-IR')}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-3">
                      <p className="text-xs font-medium text-slate-500">مبلغ کل</p>
                      <p className="mt-1 text-lg font-bold tabular-nums text-slate-900 sm:text-xl">
                        {formatRial(profile.total_amount ?? 0)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white px-4 py-3">
                      <p className="text-xs font-medium text-amber-800/80">جمع نقدی</p>
                      <p className="mt-1 text-lg font-bold tabular-nums text-amber-950 sm:text-xl">
                        {formatRial(profile.total_naghdi ?? 0)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white px-4 py-3">
                      <p className="text-xs font-medium text-sky-800/80">جمع بیمه</p>
                      <p className="mt-1 text-lg font-bold tabular-nums text-sky-950 sm:text-xl">
                        {formatRial(profile.total_bime ?? 0)}
                      </p>
                    </div>
                  </div>

                  {profile.source === 'his' && (
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <div className="border-b border-gray-100 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                        پذیرش‌های HIS
                      </div>
                      {(profile.his_visits || []).length === 0 ? (
                        <p className="px-3 py-8 text-center text-sm text-gray-500">
                          پذیرشی در این بازه یافت نشد.
                        </p>
                      ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                تاریخ
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                کد پرونده
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                نوع بیمه
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                خدمت
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                مبلغ کل
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                نقدی
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">
                                بیمه
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {(profile.his_visits || []).map((visit) => {
                              const cash = Number(visit.naghdi) || 0;
                              const ins = Number(visit.bime) || 0;
                              const total = Number(visit.total_amount) || cash + ins;
                              return (
                                <tr key={visit.patient_id} className="align-top">
                                  <td className="whitespace-nowrap px-3 py-2.5 text-sm tabular-nums text-gray-800">
                                    {visit.recept_date
                                      ? toPersianDigits(visit.recept_date)
                                      : '—'}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-sm tabular-nums text-gray-700">
                                    {visit.record_code
                                      ? toPersianDigits(visit.record_code)
                                      : '—'}
                                  </td>
                                  <td className="max-w-[9rem] px-3 py-2.5 text-sm text-gray-700">
                                    {visit.insurance_name || 'آزاد / نقدی'}
                                  </td>
                                  <td className="min-w-[12rem] px-3 py-2.5 text-sm text-gray-700">
                                    {(visit.service_names || []).join('، ') || '—'}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-sm font-medium tabular-nums text-gray-900">
                                    {formatRial(total)}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-sm tabular-nums text-amber-800">
                                    {formatRial(cash)}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-2.5 text-sm tabular-nums text-sky-800">
                                    {formatRial(ins)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <CustomerClubTour open={tourOpen} onClose={closeTour} />
      <CustomerClubSegmentsEditor
        open={editorOpen}
        initial={allSegments}
        defaults={defaultsConfig}
        minSegments={minSegments}
        maxSegments={maxSegments}
        saving={editorSaving}
        error={editorError}
        period={period}
        authHeaders={authHeaders}
        onClose={() => setEditorOpen(false)}
        onSave={(segs) => void saveSegments(segs)}
      />
    </div>
  );
};

export default AdminCustomerClub;
